import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, AlertCircle, CheckCircle, Database } from 'lucide-react';
import Logo from '../Logo';
import { supabase } from '../../lib/supabase';
import { shouldConvertDemo, convertDemoToRealAccount } from '../../utils/demoConversion';
import { useDemo } from '../../contexts/DemoContext';
import { calculateHealthScore, calculateFIREPlan, scoreToGrade, formatCurrency } from '../../utils/financeLogic';
import { getStoredDiagnosticResults } from '../../utils/diagnosticStorage';

const SignupForm = () => {
  const navigate = useNavigate();
  const { endDemo } = useDemo();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isConvertingDemo, setIsConvertingDemo] = useState(false);
  const [conversionMessage, setConversionMessage] = useState('');
  const [diagnosticSummary, setDiagnosticSummary] = useState(null);
  const hasDiagnosticSummary = diagnosticSummary?.grade && diagnosticSummary?.totalScore !== undefined;

  useEffect(() => {
    if (shouldConvertDemo()) {
      setIsConvertingDemo(true);
      setConversionMessage('Your demo data will be saved to your new account');
    }
  }, []);

  useEffect(() => {
    try {
      const parsed = getStoredDiagnosticResults();
      if (!parsed) return;
      if (parsed.summary) {
        setDiagnosticSummary(parsed.summary);
        return;
      }
      if (parsed.formData) {
        const healthScore = calculateHealthScore(parsed.formData);
        const firePlan = calculateFIREPlan(parsed.formData);
        const grade = parsed.grade || scoreToGrade(healthScore.totalScore);
        const annualSavings = Math.max(0, parsed.formData.monthlySavings * 12);
        const projectedNetWorth = firePlan.currentNetWorth + annualSavings;
        setDiagnosticSummary({
          grade,
          totalScore: healthScore.totalScore,
          currentNetWorth: firePlan.currentNetWorth,
          annualSavings,
          projectedNetWorth
        });
      }
    } catch (err) {
      setDiagnosticSummary(null);
    }
  }, []);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return null;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;

      if (data.user) {
        // If converting demo, migrate the data
        if (isConvertingDemo) {
          setSuccess('Account created! Converting your demo data...');
          const conversionResult = await convertDemoToRealAccount(data.user.id);
          
          if (conversionResult.success) {
            await endDemo(); // Clean up demo session
            setSuccess('Account created and demo data migrated successfully!');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            setError(`Account created but demo conversion failed: ${conversionResult.error}`);
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          }
        } else {
          setSuccess('Account created successfully! Please check your email to verify your account.');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      }
    } catch (error) {
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`
        }
      });
      if (error) throw error;
    } catch (error) {
      setError(error.message || 'Failed to sign up with Google');
    }
  };

  return (
    <div className="min-h-screen h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-4">
            <Link to="/">
              <Logo size="xlarge" />
            </Link>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-1">Create Account</h2>
          <p className="text-gray-600 text-sm">Start tracking your financial journey</p>
        </div>

        {isConvertingDemo && (
          <div className="bg-blue-50 border border-brand-blue rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <Database className="h-5 w-5 text-brand-blue mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                {conversionMessage}
              </p>
            </div>
          </div>
        )}

        {hasDiagnosticSummary && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">Your report is ready to save</p>
                <p className="text-xs text-gray-600">
                  Grade {diagnosticSummary.grade} • {diagnosticSummary.totalScore}/100
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase text-gray-500">Baseline net worth</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(diagnosticSummary.currentNetWorth || 0)}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
              <span>Projected 12-month net worth</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(diagnosticSummary.projectedNetWorth || 0)}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              We'll save this as your baseline and track your grade over time.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="terms"
              type="checkbox"
              required
              className="h-4 w-4 border-gray-300 rounded"
              style={{ color: 'var(--brand-blue)', accentColor: 'var(--brand-blue)' }}
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
              I agree to the{' '}
              <Link 
                to="/terms" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-blue hover:opacity-80 underline transition-colors"
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                to="/privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-blue hover:opacity-80 underline transition-colors"
              >
                Privacy Policy
              </Link>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            style={{ backgroundColor: '#4F85FF' }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or sign up with</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignup}
            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-brand-blue hover:opacity-80 transition-colors"
          >
            Sign in
          </Link>
        </p>

        <div className="mt-4 text-center text-xs text-gray-500">
          © 2025 Techbrov. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
